import { styled } from '@f8n-frontend/stitches';
import { useState } from 'react';
import { match } from 'ts-pattern';

import Flex from 'components/base/Flex';
import InputField from 'components/base/InputField';
import Text from 'components/base/Text';

import { ApiStoryGroupSize } from 'gql/api/types-api.generated';
import { youtubeUrlSchema } from 'schemas/story';
import {
  isStoryVideoEmpty,
  isStoryVideoPopulated,
  mapVideoAttributesToVideoAst,
} from 'utils/story/story';

import { LayoutChangeFn, StoryVideo } from 'types/Story';

import { ContentLayoutToolbar, StoryToolbarPositioner } from './StoryToolbar';

type StoryVideoEmptyProps = {
  id: string;
  state: 'EMPTY';
};

type StoryVideoPopulatedProps = {
  id: string;
  state: 'POPULATED';
  src: string;
  width: number;
  height: number;
};

type StoryVideoProps = StoryVideoEmptyProps | StoryVideoPopulatedProps;

const mapStoryVideoToBlockProps = (storyVideo: StoryVideo): StoryVideoProps => {
  return match<StoryVideo, StoryVideoProps>(storyVideo)
    .when(isStoryVideoPopulated, (item) => {
      return {
        state: 'POPULATED',
        id: item.id,
        src: item.content.src,
        width: item.content.width,
        height: item.content.height,
      };
    })
    .when(isStoryVideoEmpty, (item) => {
      return {
        state: 'EMPTY',
        id: item.id,
      };
    })
    .exhaustive();
};

const mapBlockPropsToStoryVideo = (props: StoryVideoProps): StoryVideo => {
  return match<StoryVideoProps, StoryVideo>(props)
    .with({ state: 'EMPTY' }, (item) => {
      return {
        type: 'VIDEO',
        id: item.id,
        placeholder: null,
        content: {
          state: 'EMPTY',
        },
      };
    })
    .with({ state: 'POPULATED' }, (item) => {
      return {
        type: 'VIDEO',
        state: 'EMPTY',
        id: item.id,
        placeholder: null,
        content: {
          state: 'POPULATED',
          src: item.src,
          width: item.width,
          height: item.height,
          ast: mapVideoAttributesToVideoAst({
            src: item.src,
            width: item.width,
            height: item.height,
          }),
        },
      };
    })
    .exhaustive();
};

type StoryVideoBlockProps = {
  onLayoutChange: LayoutChangeFn;
  layout: ApiStoryGroupSize;
  onChange: (block: StoryVideo) => void;
  block: StoryVideo;
};

export function StoryVideoBlock(props: StoryVideoBlockProps) {
  const { block, onChange } = props;

  const mappedVideoImageBlock = mapStoryVideoToBlockProps(block);
  return (
    <div>
      {match(mappedVideoImageBlock)
        .with({ state: 'EMPTY' }, () => {
          return <StoryVideoBlockEmpty onChange={onChange} block={block} />;
        })
        .with({ state: 'POPULATED' }, (item) => {
          return (
            <StoryVideoBlockPopulated
              src={item.src}
              width={item.width}
              height={item.height}
            />
          );
        })
        .exhaustive()}
      <StoryToolbarPositioner>
        <ContentLayoutToolbar
          onLayoutChange={props.onLayoutChange}
          layout={props.layout}
        />
      </StoryToolbarPositioner>
    </div>
  );
}

export function StoryVideoBlockPopulated(props: {
  src: string;
  width: number;
  height: number;
}) {
  return (
    <StoryVideoBlockContainer
      style={{
        aspectRatio: `${props.width} / ${props.height}`,
      }}
    >
      <iframe
        style={{
          display: 'block',
        }}
        width="100%"
        height="100%"
        src={props.src}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />
    </StoryVideoBlockContainer>
  );
}

function StoryVideoBlockEmpty(props: {
  block: StoryVideo;
  onChange: (block: StoryVideo) => void;
}) {
  const { onChange, block } = props;
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  const handleChange = async (value: string) => {
    try {
      const parsedVideoBlock = await youtubeUrlSchema.parseAsync(value);

      onChange(
        mapBlockPropsToStoryVideo({
          id: block.id,
          ...parsedVideoBlock,
        })
      );
    } catch (error) {
      setError(
        "Please follow the format: 'https://www.youtube.com/watch?v=0nLTnz_T3BE'"
      );
    }
  };

  return (
    <StoryVideoBlockEmptyContainer>
      <Flex
        css={{
          width: '100%',
          flexDirection: 'column',
          gap: '$2',
          maxWidth: 480,
        }}
      >
        <Text
          weight="medium"
          css={{ width: '100%', alignItems: 'center' }}
          size={1}
        >
          YouTube URL
        </Text>
        <InputField
          placeholder="e.g. https://www.youtube.com/watch?v=0nLTnz_T3BE"
          variant="primary"
          size={1}
          onPaste={async (ev) => {
            await handleChange(ev.clipboardData.getData('Text'));
          }}
          onChange={async (ev) => {
            if (ev.target.value !== '') {
              setTouched(true);
            } else {
              setTouched(false);
            }

            await handleChange(ev.target.value);
          }}
          id="video-embed"
          error={error}
          touched={touched}
        />
      </Flex>
    </StoryVideoBlockEmptyContainer>
  );
}

const StoryVideoBlockEmptyContainer = styled('div', {
  backgroundColor: '$black3',
  flex: 1,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingY: '$7',
  aspectRatio: '16 / 9',
});

const StoryVideoBlockContainer = styled('div', {
  aspectRatio: '16 / 9',
  width: '100%',
});
